import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Trophy, Clock, CheckCircle } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const Quizzes = () => {
  const { token, updateUser, user } = useContext(AuthContext);
  const { t } = useLanguage();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const fetchQuizzes = async () => {
    try {
      const response = await axios.get(`${API}/quizzes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuizzes(response.data);
    } catch (error) {
      toast.error("Failed to load quizzes");
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuiz = (quiz) => {
    setActiveQuiz(quiz);
    setAnswers({});
    setResult(null);
  };

  const handleSubmitQuiz = async () => {
    if (Object.keys(answers).length !== activeQuiz.questions.length) {
      toast.error("Please answer all questions");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/quizzes/${activeQuiz.id}/submit`,
        { answers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(response.data);
      toast.success("+" + response.data.points_earned + " points earned!");
      
      // Update user points
      const updatedUser = { ...user, points: (user.points || 0) + response.data.points_earned };
      updateUser(updatedUser);
    } catch (error) {
      toast.error("Failed to submit quiz");
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "easy": return "bg-green-100 text-green-700";
      case "medium": return "bg-yellow-100 text-yellow-700";
      case "hard": return "bg-red-100 text-red-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-8">
          <Card className="text-center">
            <CardHeader>
              <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <CardTitle className="text-3xl mb-2">Quiz Complete!</CardTitle>
              <CardDescription className="text-lg">
                You scored {result.score} out of {result.total}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 mb-6">
                <p className="text-4xl font-bold text-indigo-600 mb-2">+{result.points_earned}</p>
                <p className="text-gray-600">Points Earned</p>
              </div>
              <div className="space-y-3">
                <Button className="w-full" onClick={() => { setActiveQuiz(null); setResult(null); }}>
                  Take Another Quiz
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (activeQuiz) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between mb-4">
                <CardTitle>{activeQuiz.title}</CardTitle>
                <div className="flex items-center gap-2">
                  <Badge className={getDifficultyColor(activeQuiz.difficulty)}>
                    {activeQuiz.difficulty}
                  </Badge>
                  <Badge>{activeQuiz.points} pts</Badge>
                </div>
              </div>
              {activeQuiz.time_limit && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>Time Limit: {activeQuiz.time_limit / 60} minutes</span>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {activeQuiz.questions.map((q, idx) => (
                <div key={idx} className="space-y-3">
                  <h3 className="font-semibold text-lg">Q{idx + 1}. {q.question}</h3>
                  <RadioGroup
                    value={answers[idx]?.toString()}
                    onValueChange={(value) => setAnswers({ ...answers, [idx]: parseInt(value) })}
                  >
                    {q.options.map((option, optIdx) => (
                      <div key={optIdx} className="flex items-center space-x-3 p-3 rounded-lg border-2 hover:border-indigo-300 transition-colors">
                        <RadioGroupItem value={optIdx.toString()} id={'q' + idx + '-opt' + optIdx} />
                        <Label htmlFor={'q' + idx + '-opt' + optIdx} className="flex-1 cursor-pointer">
                          {option}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              ))}
              <div className="flex gap-4 pt-4">
                <Button variant="outline" onClick={() => setActiveQuiz(null)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleSubmitQuiz} className="flex-1">
                  Submit Quiz
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="quizzes-container">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <Trophy className="w-10 h-10 text-yellow-500" />
            {t('quizzes.title')}
          </h1>
          <p className="text-gray-600 text-lg">{t('quizzes.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {quizzes.map((quiz) => (
            <Card key={quiz.id} className="card-hover" data-testid={'quiz-card-' + quiz.id}>
              <CardHeader>
                <div className="flex items-start justify-between mb-2">
                  <Badge>{quiz.category}</Badge>
                  <Badge className={getDifficultyColor(quiz.difficulty)}>
                    {quiz.difficulty}
                  </Badge>
                </div>
                <CardTitle className="text-lg">{quiz.title}</CardTitle>
                <CardDescription>
                  {quiz.questions.length} {t('quizzes.questions')} • {quiz.points} {t('quizzes.points')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {quiz.time_limit && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{quiz.time_limit / 60} {t('quizzes.minutes')} {t('quizzes.timeLimit')}</span>
                    </div>
                  )}
                  <Button className="w-full" onClick={() => handleStartQuiz(quiz)}>
                    {t('quizzes.takeQuiz')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {quizzes.length === 0 && (
          <div className="text-center py-12" data-testid="no-quizzes-message">
            <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No quizzes available at the moment.</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Quizzes;